import React, { useMemo, useState, useEffect } from 'react'
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { useFinancialFlows } from '../context'
import { useAppTranslation } from 'src/admin/core/hooks'

/**
 * Format a number value for display with currency format (RD$)
 */
const formatCurrency = (value: number): string => {
  const formatted = new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value))
  return value < 0 ? `-${formatted}` : formatted
}

/**
 * Format a number value for display with thousands separators
 */
const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Get cell color based on column name and value
 * - Entradas: always green
 * - Salidas: always red
 * - Balance: red if negative, black if positive/zero
 */
const getCellColor = (columnName: string, value: number): string => {
  const column = columnName.toLowerCase()
  
  if (column === 'balance') {
    return value < 0 ? 'error.main' : 'text.primary' // Red if negative, black otherwise
  }
  
  return 'text.primary'
}

/**
 * Check if a column is an identifier column
 */
const isIdentifierColumn = (columnName: string): boolean => {
  const lower = columnName.toLowerCase()
  return (
    lower === 'id' ||
    lower.endsWith('id') ||
    lower.includes('code') ||
    lower.includes('codigo') ||
    lower.includes('ticket')
  )
}

/**
 * Check if a column is a date column
 */
const isDateColumn = (columnName: string): boolean => {
  const lower = columnName.toLowerCase()
  return (
    lower.includes('fecha') ||
    lower.includes('date') ||
    lower.includes('time') ||
    lower.includes('hora')
  )
}

/**
 * Get alignment based on column type
 */
const getColumnAlignment = (columnName: string, isNumeric: boolean): 'left' | 'center' | 'right' => {
  const lower = columnName.toLowerCase()
  if (lower === 'margen' || lower === 'tickets') return 'right'
  if (isDateColumn(columnName)) return 'center'
  if (isNumeric) return 'right'
  return 'left'
}

/**
 * Dynamic table component that renders columns based on data keys
 * Columns are derived from the object keys and rows from the values
 */
export const ReportDataTable: React.FC = () => {
  const { label } = useAppTranslation()
  const { reportData, reportDataLoading, reportDataError, selectedReportType, filters } = useFinancialFlows()

  // Detect mobile breakpoint
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Debounced filter text state
  const [debouncedFilterText, setDebouncedFilterText] = useState('')

  // Get the current filter text based on report type
  const currentFilterText = selectedReportType?.AgencyFilter 
    ? filters.agencyFilterText 
    : selectedReportType?.ProductFilter 
      ? filters.productFilterText 
      : ''

  // Debounce the filter text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterText(currentFilterText)
    }, 300)

    return () => clearTimeout(timer)
  }, [currentFilterText])

  // Extract column headers dynamically from the first data item
  const columns = useMemo(() => {
    if (!reportData || reportData.length === 0) return []
    return Object.keys(reportData[0])
  }, [reportData])

  // Filter data based on debounced filter text (filters the first column - entity name)
  const filteredData = useMemo(() => {
    if (!reportData || reportData.length === 0) return []
    if (!debouncedFilterText.trim()) return reportData

    const filterLower = debouncedFilterText.toLowerCase().trim()
    
    return reportData.filter((row) => {
      // Get the first column value (entity name: Agencia, Concepto, Producto, etc.)
      const firstColumnKey = columns[0]
      if (!firstColumnKey) return true
      
      const value = (row as unknown as Record<string, unknown>)[firstColumnKey]
      if (typeof value === 'string') {
        return value.toLowerCase().includes(filterLower)
      }
      return true
    })
  }, [reportData, debouncedFilterText, columns])

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Handle sort click
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, start with ascending
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Sorted data based on current sort state
  const sortedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return []
    if (!sortColumn) return filteredData

    return [...filteredData].sort((a, b) => {
      const aValue = (a as unknown as Record<string, unknown>)[sortColumn]
      const bValue = (b as unknown as Record<string, unknown>)[sortColumn]

      // Handle numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
      }

      // Handle string values
      const aStr = String(aValue ?? '').toLowerCase()
      const bStr = String(bValue ?? '').toLowerCase()
      
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr)
      } else {
        return bStr.localeCompare(aStr)
      }
    })
  }, [filteredData, sortColumn, sortDirection])

  // Calculate totals for numeric columns (based on filtered data)
  const totals = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return {}
    
    const result: Record<string, number> = {}
    
    columns.forEach((column) => {
      // Skip identifier columns for totals
      if (isIdentifierColumn(column)) return

      const firstValue = (reportData[0] as unknown as Record<string, unknown>)[column]
      if (typeof firstValue === 'number') {
        result[column] = filteredData.reduce((sum, row) => {
          const value = (row as unknown as Record<string, unknown>)[column]
          return sum + (typeof value === 'number' ? value : 0)
        }, 0)
      }
    })
    
    return result
  }, [filteredData, columns, reportData])

  // Get numeric columns for the totals display
  const numericColumns = useMemo(() => {
    return columns.filter((col) => totals[col] !== undefined)
  }, [columns, totals])

  // Loading state
  if (reportDataLoading) {
    return (
      <Card sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Card>
    )
  }

  // Error state
  if (reportDataError) {
    return (
      <Card sx={{ p: 2 }}>
        <Alert severity="error">{reportDataError}</Alert>
      </Card>
    )
  }

  // Empty state - no data yet or no results
  if (!reportData || reportData.length === 0) {
    return null
  }

  return (
    <Card>
      {/* Summary Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '4px solid',
          borderColor: 'primary.main',
          backgroundColor: 'grey.50',
        }}
      >
        {/* Title Row */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            {label('TOTAL_GENERAL')}
          </Typography>
          <Chip
            label={`${filteredData.length} ${label('REGISTROS_ENCONTRADOS') || 'registros encontrados'}`}
            color="success"
            size="small"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Combined Data Table with Totals */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            {/* Totals Row */}
            <TableRow
              sx={{
                backgroundColor: 'background.paper',
                borderBottom: '2px solid',
                borderBottomColor: 'divider',
              }}
            >
              {isMobile ? (
                // Mobile: Only show numeric columns
                <>
                  {numericColumns.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        whiteSpace: 'nowrap',
                        py: 1,
                        px: 0.5,
                        fontSize: '0.75rem',
                        color: getCellColor(column, totals[column]),
                      }}
                      align="right"
                    >
                      {formatCurrency(totals[column])}
                    </TableCell>
                  ))}
                </>
              ) : (
                // Desktop: Show all columns
                columns.map((column, index) => {
                  const isNumeric = totals[column] !== undefined
                  const isFirstColumn = index === 0
                  const alignment = getColumnAlignment(column, isNumeric)

                  return (
                    <TableCell
                      key={column}
                      sx={{
                        whiteSpace: 'nowrap',
                        py: 1,
                        fontSize: '0.875rem',
                        color: isNumeric ? getCellColor(column, totals[column]) : 'text.primary',
                      }}
                      align={alignment}
                    >
                      {isFirstColumn
                        ? ''
                        : isNumeric
                          ? formatCurrency(totals[column])
                          : ''}
                    </TableCell>
                  )
                })
              )}
            </TableRow>
            {/* Column Headers Row */}
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              {isMobile ? (
                // Mobile: Only show numeric column headers
                numericColumns.map((column) => (
                  <TableCell
                    key={column}
                      sx={{
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderBottomColor: 'divider',
                        whiteSpace: 'nowrap',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        cursor: 'pointer',
                        px: 0.5,
                        fontSize: '0.7rem',
                        '&:hover': { backgroundColor: 'grey.200' },
                      }}
                    align="right"
                    sortDirection={sortColumn === column ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={sortColumn === column}
                      direction={sortColumn === column ? sortDirection : 'asc'}
                      onClick={() => handleSort(column)}
                    >
                      {column}
                    </TableSortLabel>
                  </TableCell>
                ))
              ) : (
                // Desktop: Show all columns
                columns.map((column) => {
                  const isNumeric = totals[column] !== undefined
                  const alignment = getColumnAlignment(column, isNumeric)
                  
                  return (
                    <TableCell
                      key={column}
                      sx={{
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderBottomColor: 'divider',
                        whiteSpace: 'nowrap',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'grey.200' },
                      }}
                      align={alignment}
                      sortDirection={sortColumn === column ? sortDirection : false}
                    >
                      <TableSortLabel
                        active={sortColumn === column}
                        direction={sortColumn === column ? sortDirection : 'asc'}
                        onClick={() => handleSort(column)}
                        // Adjust icon position based on alignment if needed, but default is usually fine
                      >
                        {column}
                      </TableSortLabel>
                    </TableCell>
                  )
                })
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, rowIndex) => {
              const firstColumnKey = columns[0]
              const firstColumnValue = firstColumnKey 
                ? (row as unknown as Record<string, unknown>)[firstColumnKey] 
                : ''

              return isMobile ? (
                // Mobile: Two rows per record
                <React.Fragment key={rowIndex}>
                  {/* Entity name row (Agencia, Producto, etc.) */}
                  <TableRow
                    sx={{
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <TableCell
                      colSpan={numericColumns.length}
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        color: 'text.primary',
                        borderBottom: 'none',
                        py: 1,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                      }}
                    >
                      {String(firstColumnValue ?? '')}
                    </TableCell>
                  </TableRow>
                  {/* Values row */}
                  <TableRow
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    {numericColumns.map((column) => {
                      const value = (row as unknown as Record<string, unknown>)[column]
                      const numValue = typeof value === 'number' ? value : 0

                      return (
                        <TableCell
                          key={column}
                          sx={{
                            whiteSpace: 'nowrap',
                            color: getCellColor(column, numValue),
                            borderBottom: '1px solid',
                            borderBottomColor: 'divider',
                            px: 0.5,
                            fontSize: '0.8rem',
                          }}
                          align="right"
                        >
                          {formatCurrency(numValue)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                </React.Fragment>
              ) : (
                // Desktop: Single row per record
                <TableRow
                  key={rowIndex}
                  sx={{
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {columns.map((column) => {
                    const value = (row as unknown as Record<string, unknown>)[column]
                    const isNumber = typeof value === 'number'
                    // IMPORTANT: use totals check or manual check for numeric column status to be consistent with header
                    // But here checking value type is safer for the specific cell rendering
                    // We need alignment consistency.
                    const isNumericColumn = totals[column] !== undefined 
                    const alignment = getColumnAlignment(column, isNumericColumn)

                    return (
                      <TableCell
                        key={column}
                        sx={{
                          whiteSpace: 'nowrap',
                          color: isNumber ? getCellColor(column, value as number) : 'text.primary',
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider',
                        }}
                        align={alignment}
                      >
                        {isNumber ? formatCurrency(value as number) : String(value ?? '')}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  )
}

export default ReportDataTable

